/**
 * Hook para exportação de DRE e versões para Excel/CSV
 * 
 * @module hooks/useExportDRE
 * @description Fornece funções para exportar dados DRE editáveis
 * para formatos Excel (.xlsx) e CSV, incluindo histórico de versões
 */

import { useCallback } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useDREStore } from '../stores/dreStore';
import { useVersionStore } from '../stores/versionStore';
import { DREYear, DRECompany, DREData } from '../types/dre';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';

/**
 * Hook principal de exportação
 */
export function useExportDRE() {
  const { dreData } = useDREStore();
  const { versions } = useVersionStore();

  /**
   * Exporta DRE atual para Excel
   */
  const exportDREToExcel = useCallback(() => {
    if (!dreData) {
      toast.error('Nenhuma DRE carregada para exportar');
      return;
    }

    try {
      // Criar workbook
      const wb = XLSX.utils.book_new();

      // Criar sheet para Sétima
      const setimaSheet = createDRESheet(dreData.setima);
      XLSX.utils.book_append_sheet(wb, setimaSheet, 'Sétima');

      // Criar sheet para 555 Studios
      const studio555Sheet = createDRESheet(dreData.studio555);
      XLSX.utils.book_append_sheet(wb, studio555Sheet, '555 Studios');

      // Criar sheet de resumo
      const summarySheet = createSummarySheet(dreData);
      XLSX.utils.book_append_sheet(wb, summarySheet, 'Resumo');

      // Gerar arquivo
      const timestamp = format(new Date(), 'yyyy-MM-dd_HHmm', { locale: ptBR });
      const filename = `DRE_Valuation_${timestamp}.xlsx`;
      
      XLSX.writeFile(wb, filename);
      
      toast.success(`DRE exportada: ${filename}`);
    } catch (error) {
      console.error('Erro ao exportar DRE:', error);
      toast.error('Erro ao exportar DRE para Excel');
    }
  }, [dreData]);

  /**
   * Exporta histórico de versões para CSV
   */
  const exportVersionsToCSV = useCallback(() => {
    if (versions.length === 0) {
      toast.error('Nenhuma versão para exportar');
      return;
    }

    try {
      // Criar dados CSV
      const csvData = versions.map((version) => ({
        'Versão': version.versionNumber,
        'Nome': version.name,
        'Descrição': version.description || '',
        'Data': format(version.createdAt, 'dd/MM/yyyy HH:mm', { locale: ptBR }),
        'Autor': version.createdBy,
        'Alterações': version.diff ? version.diff.length : 0,
      }));

      // Converter para worksheet
      const ws = XLSX.utils.json_to_sheet(csvData);
      
      // Ajustar largura das colunas
      ws['!cols'] = [
        { wch: 8 },   // Versão
        { wch: 30 },  // Nome
        { wch: 50 },  // Descrição
        { wch: 18 },  // Data
        { wch: 30 },  // Autor
        { wch: 12 },  // Alterações
      ];

      // Converter para CSV
      const csv = XLSX.utils.sheet_to_csv(ws);
      
      // Salvar arquivo
      const timestamp = format(new Date(), 'yyyy-MM-dd_HHmm', { locale: ptBR });
      const filename = `Historico_Versoes_DRE_${timestamp}.csv`;
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      
      saveAs(blob, filename);
      
      toast.success(`Histórico exportado: ${filename}`);
    } catch (error) {
      console.error('Erro ao exportar versões:', error);
      toast.error('Erro ao exportar histórico de versões');
    }
  }, [versions]);

  /**
   * Exporta versão específica para Excel
   */
  const exportVersionToExcel = useCallback((versionId: string) => {
    const version = versions.find((v) => v.id === versionId);
    
    if (!version) {
      toast.error('Versão não encontrada');
      return;
    }

    try {
      // Criar workbook
      const wb = XLSX.utils.book_new();

      // Sheets de DRE
      const setimaSheet = createDRESheet(version.dreData.setima);
      XLSX.utils.book_append_sheet(wb, setimaSheet, 'Sétima');

      const studio555Sheet = createDRESheet(version.dreData.studio555);
      XLSX.utils.book_append_sheet(wb, studio555Sheet, '555 Studios');

      // Sheet de metadados
      const metadataSheet = XLSX.utils.json_to_sheet([
        { Campo: 'Versão', Valor: version.versionNumber },
        { Campo: 'Nome', Valor: version.name },
        { Campo: 'Descrição', Valor: version.description || 'N/A' },
        { Campo: 'Data', Valor: format(version.createdAt, 'dd/MM/yyyy HH:mm', { locale: ptBR }) },
        { Campo: 'Autor', Valor: version.createdBy },
      ]);
      XLSX.utils.book_append_sheet(wb, metadataSheet, 'Metadados');

      // Gerar arquivo
      const timestamp = format(version.createdAt, 'yyyy-MM-dd_HHmm', { locale: ptBR });
      const filename = `DRE_v${version.versionNumber}_${timestamp}.xlsx`;
      
      XLSX.writeFile(wb, filename);
      
      toast.success(`Versão exportada: ${filename}`);
    } catch (error) {
      console.error('Erro ao exportar versão:', error);
      toast.error('Erro ao exportar versão para Excel');
    }
  }, [versions]);

  /**
   * Exporta comparação entre duas versões
   */
  const exportComparison = useCallback((v1Id: string, v2Id: string) => {
    const v1 = versions.find((v) => v.id === v1Id);
    const v2 = versions.find((v) => v.id === v2Id);

    if (!v1 || !v2) {
      toast.error('Versões não encontradas');
      return;
    }

    try {
      const wb = XLSX.utils.book_new();

      // Sheet de comparação Sétima
      const setimaComparison = createComparisonSheet(
        v1.dreData.setima,
        v2.dreData.setima,
        `v${v1.versionNumber}`,
        `v${v2.versionNumber}`
      );
      XLSX.utils.book_append_sheet(wb, setimaComparison, 'Sétima - Comparação');

      // Sheet de comparação 555
      const studio555Comparison = createComparisonSheet(
        v1.dreData.studio555,
        v2.dreData.studio555,
        `v${v1.versionNumber}`,
        `v${v2.versionNumber}`
      );
      XLSX.utils.book_append_sheet(wb, studio555Comparison, '555 - Comparação');

      // Gerar arquivo
      const filename = `Comparacao_v${v1.versionNumber}_v${v2.versionNumber}.xlsx`;
      XLSX.writeFile(wb, filename);
      
      toast.success(`Comparação exportada: ${filename}`);
    } catch (error) {
      console.error('Erro ao exportar comparação:', error);
      toast.error('Erro ao exportar comparação');
    }
  }, [versions]);

  return {
    exportDREToExcel,
    exportVersionsToCSV,
    exportVersionToExcel,
    exportComparison,
  };
}

/**
 * Cria worksheet para uma empresa
 */
function createDRESheet(company: DRECompany): XLSX.WorkSheet {
  const years = company.years.sort((a, b) => a.year - b.year);
  
  // Dados em formato de tabela
  const data = [
    ['Linha DRE', ...years.map((y) => y.year)],
    ['Receita Bruta', ...years.map((y) => y.receitaBruta)],
    ['(-) Impostos s/ Vendas', ...years.map((y) => -y.impostosVendas)],
    ['= Receita Líquida', ...years.map((y) => y.receitaLiquida)],
    ['(-) Custos Diretos', ...years.map((y) => -y.custosDirectos)],
    ['= Lucro Bruto', ...years.map((y) => y.lucroBruto)],
    ['% Margem Bruta', ...years.map((y) => y.margemBruta / 100)],
    ['(-) Despesas Operacionais', ...years.map((y) => -y.despesasOperacionais)],
    ['= EBITDA', ...years.map((y) => y.ebitda)],
    ['% Margem EBITDA', ...years.map((y) => y.margemEbitda / 100)],
    ['(-) Depreciação', ...years.map((y) => -y.depreciacao)],
    ['= EBIT', ...years.map((y) => y.ebit)],
    ['% Margem EBIT', ...years.map((y) => y.margemEbit / 100)],
    ['(-) Impostos s/ Lucro', ...years.map((y) => -y.impostosLucro)],
    ['= Lucro Líquido', ...years.map((y) => y.lucroLiquido)],
    ['% Margem Líquida', ...years.map((y) => y.margemLiquida / 100)],
  ];

  const ws = XLSX.utils.aoa_to_sheet(data);

  // Formatar células de moeda (colunas B em diante, linhas 2-16, exceto %)
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  for (let R = 1; R <= range.e.r; R++) {
    for (let C = 1; C <= range.e.c; C++) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      if (!ws[cellAddress]) continue;

      // Aplicar formato baseado na linha
      const rowData = data[R];
      const firstCol = rowData[0];
      if (typeof firstCol === 'string' && firstCol.includes('%')) {
        // Formato percentual
        ws[cellAddress].z = '0.00%';
      } else {
        // Formato moeda
        ws[cellAddress].z = 'R$ #,##0.00';
      }
    }
  }

  // Ajustar largura das colunas
  ws['!cols'] = [
    { wch: 30 }, // Linha DRE
    ...years.map(() => ({ wch: 15 })), // Anos
  ];

  return ws;
}

/**
 * Cria worksheet de resumo
 */
function createSummarySheet(dreData: DREData): XLSX.WorkSheet {
  const setimaYears = dreData.setima.years;
  const studio555Years = dreData.studio555.years;

  const data = [
    ['Resumo Executivo'],
    [],
    ['Empresa', 'Anos', 'Receita Total', 'EBITDA Médio', 'Margem EBITDA Média'],
    [
      'Sétima',
      setimaYears.length,
      setimaYears.reduce((sum: number, y: DREYear) => sum + y.receitaBruta, 0),
      setimaYears.reduce((sum: number, y: DREYear) => sum + y.ebitda, 0) / setimaYears.length,
      setimaYears.reduce((sum: number, y: DREYear) => sum + y.margemEbitda, 0) / setimaYears.length / 100,
    ],
    [
      '555 Studios',
      studio555Years.length,
      studio555Years.reduce((sum: number, y: DREYear) => sum + y.receitaBruta, 0),
      studio555Years.reduce((sum: number, y: DREYear) => sum + y.ebitda, 0) / studio555Years.length,
      studio555Years.reduce((sum: number, y: DREYear) => sum + y.margemEbitda, 0) / studio555Years.length / 100,
    ],
    [],
    ['Exportado em:', format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })],
  ];

  const ws = XLSX.utils.aoa_to_sheet(data);

  // Formatar células
  ws['C4'].z = 'R$ #,##0.00';
  ws['C5'].z = 'R$ #,##0.00';
  ws['D4'].z = 'R$ #,##0.00';
  ws['D5'].z = 'R$ #,##0.00';
  ws['E4'].z = '0.00%';
  ws['E5'].z = '0.00%';

  ws['!cols'] = [
    { wch: 15 }, // Empresa
    { wch: 10 }, // Anos
    { wch: 18 }, // Receita Total
    { wch: 18 }, // EBITDA Médio
    { wch: 20 }, // Margem EBITDA Média
  ];

  return ws;
}

/**
 * Cria worksheet de comparação entre duas versões
 */
function createComparisonSheet(
  company1: DRECompany,
  company2: DRECompany,
  label1: string,
  label2: string
): XLSX.WorkSheet {
  const years1 = company1.years.sort((a, b) => a.year - b.year);
  const years2 = company2.years.sort((a, b) => a.year - b.year);

  // Encontrar anos comuns
  const commonYears = years1
    .filter((y1) => years2.some((y2) => y2.year === y1.year))
    .map((y) => y.year);

  const data: Array<Array<string | number>> = [
    ['Linha DRE', ...commonYears.flatMap((year) => [`${year} (${label1})`, `${year} (${label2})`, 'Δ %'])],
  ];

  const fields: Array<[string, keyof DREYear]> = [
    ['Receita Bruta', 'receitaBruta'],
    ['EBITDA', 'ebitda'],
    ['% Margem EBITDA', 'margemEbitda'],
    ['EBIT', 'ebit'],
    ['Lucro Líquido', 'lucroLiquido'],
    ['% Margem Líquida', 'margemLiquida'],
  ];

  fields.forEach(([label, field]) => {
    const row: Array<string | number> = [label];
    commonYears.forEach((year) => {
      const y1 = years1.find((y) => y.year === year);
      const y2 = years2.find((y) => y.year === year);

      if (y1 && y2) {
        const val1 = y1[field] as number;
        const val2 = y2[field] as number;
        const delta = val1 !== 0 ? ((val2 - val1) / val1) * 100 : 0;

        row.push(val1, val2, delta);
      } else {
        row.push('N/A', 'N/A', 'N/A');
      }
    });
    data.push(row);
  });

  const ws = XLSX.utils.aoa_to_sheet(data);

  // Ajustar largura
  ws['!cols'] = [
    { wch: 25 }, // Label
    ...commonYears.flatMap(() => [{ wch: 18 }, { wch: 18 }, { wch: 12 }]),
  ];

  return ws;
}
